import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { FileText, FileSpreadsheet, ScanLine, Clock, AlertCircle } from "lucide-react";

export default function PublicShare() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Use raw axios (no auth)
        const { data } = await api.get(`/share/${token}`);
        setMeta(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Lien inaccessible");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const dl = (kind) => {
    window.location.href = `${API}/share/${token}/${kind}`;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="public-share-page">
      <header className="border-b border-gray-200 sticky top-0 z-40 bg-white/90 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-2">
          <div className="w-7 h-7 bg-[#002FA7] flex items-center justify-center">
            <ScanLine className="text-white" size={16} />
          </div>
          <span className="font-heading font-black text-lg tracking-tighter">FACTURÉ</span>
          <span className="ml-3 overline">Partage public</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading && <div className="progress-line max-w-sm" />}

        {err && (
          <div className="ra-card p-8 border-red-200">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="text-red-600" size={22} />
              <div className="font-heading text-2xl font-bold">Lien indisponible</div>
            </div>
            <p className="text-gray-600">{err}</p>
            <p className="text-sm text-gray-400 mt-6">Le lien a peut-être expiré ou été révoqué par l'expéditeur.</p>
          </div>
        )}

        {meta && (
          <div>
            <p className="overline mb-2">Partagé par {meta.created_by_name || "—"}</p>
            <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none mb-3">
              {meta.title || meta.report_name || (meta.month ? `Compte de dépenses · ${meta.month}` : "Compte de dépenses")}
            </h1>
            <div className="flex items-center gap-3 text-sm text-gray-500 mb-10">
              <span className="num">{meta.invoice_count} facture(s)</span>
              <span>·</span>
              <span className="num">{Number(meta.total_sum || 0).toFixed(2)} $</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Clock size={12} /> Expire le {String(meta.expires_at).slice(0, 10)}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {meta.types.includes("pdf") && (
                <button
                  onClick={() => dl("pdf")}
                  className="ra-card p-8 text-left hover:border-[#002FA7] transition-colors"
                  data-testid="share-download-pdf"
                >
                  <div className="w-10 h-10 bg-[#002FA7] text-white flex items-center justify-center mb-4">
                    <FileText size={20} />
                  </div>
                  <p className="overline mb-1">Document</p>
                  <div className="font-heading text-2xl font-bold tracking-tight mb-1">Télécharger PDF</div>
                  <div className="text-sm text-gray-500">Toutes les photos de factures, triées par date.</div>
                </button>
              )}
              {meta.types.includes("excel") && (
                <button
                  onClick={() => dl("excel")}
                  className="ra-card p-8 text-left hover:border-[#002FA7] transition-colors"
                  data-testid="share-download-excel"
                >
                  <div className="w-10 h-10 bg-black text-white flex items-center justify-center mb-4">
                    <FileSpreadsheet size={20} />
                  </div>
                  <p className="overline mb-1">Tableur</p>
                  <div className="font-heading text-2xl font-bold tracking-tight mb-1">Télécharger Excel</div>
                  <div className="text-sm text-gray-500">Format Riva-Avir, colonnes et taxes calculées.</div>
                </button>
              )}
            </div>

            <p className="text-xs text-gray-400 mt-12">
              Ce lien est public et n'exige aucun compte. Il restera actif jusqu'au {String(meta.expires_at).slice(0, 10)}.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
